import {FC, useEffect, useRef, useState} from 'react'
import Swal from 'sweetalert2'
import {api} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'

const toast = (title: string, icon: 'success' | 'error' = 'success') =>
  Swal.fire({
    toast: true, position: 'top-end', icon, title,
    showConfirmButton: false, timer: 2600, timerProgressBar: true,
  })

interface Interest {
  id: number
  label: string
  icon: string
  order: number
  isActive: boolean
}

const ICONS: string[] = [
  'basketball','soccer','football','volleyball','tennis','golf','baseball','hockey-sticks',
  'swim','bicycle','run','hiking','skiing','snowboard','surfing','yoga','weight-lifter',
  'martial-arts','archery','bowling','darts','fishing','horse','rock-climbing',
  'skateboarding','boxing-glove','table-tennis','badminton','cricket','rugby',
  'music-note','music-note-eighth','music-note-sixteenth','guitar-acoustic','guitar-electric',
  'piano','violin','saxophone','trumpet','microphone','microphone-variant',
  'headphones','speaker','album','radio','music-circle',
  'palette','brush','pencil','pen','draw','image','camera','camera-iris','film',
  'theater','dance-ballroom','book-open-variant','library','museum','castle',
  'brush-variant','spray','scissors-cutting',
  'food','food-apple','food-variant','pizza','hamburger','noodles',
  'coffee','coffee-maker','beer','beer-outline','wine','cocktail',
  'cake','ice-cream','candy','cupcake','cheese','fish','shrimp',
  'silverware-fork-knife','silverware-variant','chef-hat',
  'airplane','airplane-takeoff','car','car-sports','train','bus','ship',
  'map-marker','compass','passport','bag-suitcase','tent','map',
  'road-variant','motorcycle','rv-truck','sail-boat',
  'tree','leaf','flower','flower-tulip','mountain','beach','nature',
  'sun-wireless','weather-sunny','weather-night','weather-rainy',
  'fire','water','snowflake','cloud','earth','forest',
  'account-group','human-greeting','heart','hand-heart','handshake',
  'party-popper','gift','balloon','firework','clover',
  'emoticon-happy','emoticon-cool','star','star-shooting',
  'gamepad-variant','gamepad-variant-outline','controller-classic',
  'laptop','code-braces','robot','chip','wifi','cellphone',
  'monitor','keyboard','mouse','printer','drone','virtual-reality',
  'movie-open','popcorn','ticket-confirmation','television','youtube',
  'twitch','podcast','play-circle','cinema','projector','clapper-board',
  'heart-pulse','meditation','spa','pill','hospital','dumbbell',
  'run-fast','weight','apple','leaf-circle','bandage',
  'briefcase','school','school-outline','book','book-education',
  'graduation-cap','presentation','chart-bar','office-building',
  'currency-usd','cash','bank','lightbulb','rocket-launch',
  'camera-outline','image-multiple','vector-square','ruler',
  'palette-swatch','layers','shape','grid','eye',
  'dog','cat','rabbit','bird','fish-outline','horse-variant','paw',
  'puzzle','chess-queen','cards','dice-multiple','telescope',
  'recycle','seedling','flower-pollen','pine-tree','candle',
  'coffee-outline','tea','bottle-wine','glass-wine',
]

// ── Icon Picker ───────────────────────────────────────────────────────────────
const IconPicker: FC<{value: string; onChange: (icon: string) => void}> = ({value, onChange}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const filtered = search ? ICONS.filter(ic => ic.includes(search.toLowerCase())) : ICONS

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  return (
    <div ref={ref} style={{position: 'relative'}}>
      <div onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer',
          background: '#f8fafc', minHeight: 44,
        }}>
        {value ? (
          <>
            <div style={{width: 28, height: 28, borderRadius: 6, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
              <i className={`mdi mdi-${value}`} style={{fontSize: 18, color: '#3b82f6'}} />
            </div>
            <span style={{fontSize: 14, color: '#0f172a', fontWeight: 500}}>{value}</span>
          </>
        ) : (
          <span style={{fontSize: 14, color: '#94a3b8'}}>Select an icon...</span>
        )}
        <svg style={{marginLeft: 'auto', color: '#94a3b8', flexShrink: 0}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <polyline points={open ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4,
          display: 'flex', flexDirection: 'column', maxHeight: 320,
        }}>
          <div style={{padding: '10px 12px', borderBottom: '1px solid #f1f5f9'}}>
            <div style={{position: 'relative'}}>
              <svg style={{position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
              </svg>
              <input autoFocus placeholder='Search icons...' value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box'}} />
            </div>
          </div>
          <div style={{flex: 1, overflowY: 'auto', padding: 8}}>
            {filtered.length === 0 ? (
              <div style={{textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13}}>No icons found</div>
            ) : (
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                {filtered.map(ic => (
                  <div key={ic} title={ic}
                    onClick={() => { onChange(ic); setOpen(false); setSearch('') }}
                    style={{
                      width: 64, display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', padding: '6px 4px', borderRadius: 8, cursor: 'pointer',
                      background: ic === value ? '#3b82f6' : '#f8fafc',
                      border: ic === value ? '1px solid #3b82f6' : '1px solid transparent',
                    }}
                    onMouseEnter={e => { if (ic !== value) (e.currentTarget as HTMLElement).style.background = '#eff6ff' }}
                    onMouseLeave={e => { if (ic !== value) (e.currentTarget as HTMLElement).style.background = '#f8fafc' }}
                  >
                    <i className={`mdi mdi-${ic}`} style={{fontSize: 20, color: ic === value ? '#fff' : '#374151'}} />
                    <span style={{fontSize: 9, marginTop: 3, color: ic === value ? '#fff' : '#94a3b8', textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.2}}>
                      {ic}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{padding: '6px 12px', borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#94a3b8'}}>
            {filtered.length} icons · MaterialCommunityIcons
          </div>
        </div>
      )}
    </div>
  )
}

// ── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle: FC<{checked: boolean; onChange: () => void}> = ({checked, onChange}) => (
  <div onClick={onChange} style={{
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', flexShrink: 0,
    background: checked ? '#22c55e' : '#e2e8f0',
    position: 'relative', transition: 'background 0.2s',
  }}>
    <div style={{
      position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
    }} />
  </div>
)

// ── Main Page ─────────────────────────────────────────────────────────────────
const InterestsPage: FC = () => {
  const {items: interests, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<Interest>((p, l, s) => api.getEventTypesPaged(p, l, s), {limit: 20})

  const [showModal, setShowModal]       = useState(false)
  const [editTarget, setEditTarget]     = useState<Interest | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Interest | null>(null)
  const [saving, setSaving]             = useState(false)

  const [formLabel, setFormLabel]   = useState('')
  const [formIcon, setFormIcon]     = useState('')
  const [formOrder, setFormOrder]   = useState('')
  const [formActive, setFormActive] = useState(true)
  const [formError, setFormError]   = useState('')

  const openAdd = () => {
    setEditTarget(null)
    setFormLabel(''); setFormIcon(''); setFormOrder(''); setFormActive(true); setFormError('')
    setShowModal(true)
  }

  const openEdit = (i: Interest) => {
    setEditTarget(i)
    setFormLabel(i.label); setFormIcon(i.icon); setFormOrder(String(i.order)); setFormActive(i.isActive); setFormError('')
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!formLabel.trim()) { setFormError('Label is required.'); return }
    if (!formIcon.trim())  { setFormError('Please select an icon.'); return }
    setSaving(true)
    try {
      if (editTarget) {
        await api.updateEventType(editTarget.id, {
          label: formLabel.trim(), icon: formIcon.trim(),
          order: formOrder ? parseInt(formOrder) : editTarget.order,
          isActive: formActive,
        })
      } else {
        await api.createEventType({
          label: formLabel.trim(), icon: formIcon.trim(),
          order: formOrder ? parseInt(formOrder) : total + 1,
        })
      }
      const wasEdit = !!editTarget
      setShowModal(false)
      reload()
      toast(`Event type ${wasEdit ? 'updated' : 'created'}`)
    } catch (e: any) {
      setFormError(e.message ?? 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    const label = deleteTarget.label
    try {
      await api.deleteEventType(deleteTarget.id)
      setDeleteTarget(null)
      reload()
      toast(`"${label}" deleted`)
    } catch (e: any) {
      setDeleteTarget(null)
      toast(e?.message ?? 'Failed to delete category', 'error')
    }
  }

  const toggleStatus = async (interest: Interest) => {
    try {
      await api.updateEventType(interest.id, {isActive: !interest.isActive})
      reload()
      toast(`"${interest.label}" ${!interest.isActive ? 'activated' : 'deactivated'}`)
    } catch (e: any) {
      toast(e?.message ?? 'Failed to update', 'error')
    }
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc',
    outline: 'none', boxSizing: 'border-box' as const,
  }

  const labelStyle = {
    fontSize: 13, fontWeight: 600 as const, color: '#374151',
    display: 'block' as const, marginBottom: 6,
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Event Types</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage event categories shown in "Select Event Type" when creating an event</p>
      </div>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: 1}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input type='text' placeholder='Search categories...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{...inputStyle, paddingLeft: 38, background: '#fff'}} />
        </div>
        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
        <button onClick={openAdd}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', background: '#3b82f6', cursor: 'pointer'}}>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
          Add Category
        </button>
      </div>

      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['ORDER', 'CATEGORY', 'ICON', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {interests.map((item, i) => (
                  <tr key={item.id}
                    style={{borderBottom: i < interests.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                    onMouseEnter={ev => (ev.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                  >
                    {/* Order */}
                    <td style={{padding: '14px 20px', width: 80}}>
                      <span style={{fontSize: 13, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '3px 10px', borderRadius: 20}}>
                        #{item.order}
                      </span>
                    </td>

                    {/* Category */}
                    <td style={{padding: '14px 20px', minWidth: 180}}>
                      <span style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{item.label}</span>
                    </td>

                    {/* Icon */}
                    <td style={{padding: '14px 20px', minWidth: 160}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <div style={{width: 36, height: 36, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                          <i className={`mdi mdi-${item.icon}`} style={{fontSize: 20, color: '#3b82f6'}} />
                        </div>
                        <span style={{fontSize: 12, color: '#64748b', fontFamily: 'monospace'}}>{item.icon}</span>
                      </div>
                    </td>

                    {/* Status */}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <Toggle checked={item.isActive} onChange={() => toggleStatus(item)} />
                        <span style={{fontSize: 13, fontWeight: 500, color: item.isActive ? '#16a34a' : '#94a3b8'}}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{padding: '14px 20px'}}>
                      <div style={{display: 'flex', gap: 8}}>
                        <button onClick={() => openEdit(item)}
                          style={{padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', cursor: 'pointer'}}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f8fafc' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                          Edit
                        </button>
                        <button onClick={() => setDeleteTarget(item)}
                          style={{padding: '6px 14px', border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#ef4444', background: '#fff', cursor: 'pointer'}}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fef2f2' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {interests.length === 0 && (
                  <tr><td colSpan={5} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>No categories found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9'}}>
            <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div onClick={() => setShowModal(false)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 500, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', overflow: 'hidden'}}>
            {/* Header */}
            <div style={{padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0}}>
                {editTarget ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4}}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
              </button>
            </div>

            {/* Body */}
            <div style={{padding: '24px', display: 'flex', flexDirection: 'column', gap: 18}}>
              {formError && (
                <div style={{padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13}}>
                  {formError}
                </div>
              )}

              <div>
                <label style={labelStyle}>Label <span style={{color: '#ef4444'}}>*</span></label>
                <input placeholder='e.g. Music' value={formLabel} onChange={e => setFormLabel(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Icon <span style={{color: '#ef4444'}}>*</span></label>
                <IconPicker value={formIcon} onChange={setFormIcon} />
                <div style={{fontSize: 11, color: '#94a3b8', marginTop: 6}}>MaterialCommunityIcons — same library used in the mobile app</div>
              </div>

              <div>
                <label style={labelStyle}>Display Order</label>
                <input type='number' placeholder='e.g. 1' value={formOrder}
                  onChange={e => setFormOrder(e.target.value)} style={inputStyle} />
              </div>

              {editTarget && (
                <div>
                  <label style={labelStyle}>Status</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <Toggle checked={formActive} onChange={() => setFormActive(a => !a)} />
                    <span style={{fontSize: 14, fontWeight: 500, color: formActive ? '#16a34a' : '#94a3b8'}}>
                      {formActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10}}>
              <button onClick={() => setShowModal(false)} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{padding: '9px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1}}>
                {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div onClick={() => setDeleteTarget(null)}
          style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20}}>
          <div onClick={e => e.stopPropagation()}
            style={{background: '#fff', borderRadius: 16, width: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', padding: '32px 28px', textAlign: 'center'}}>
            <div style={{width: 56, height: 56, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'}}>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#ef4444' strokeWidth='2'>
                <polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14H6L5 6'/>
                <path d='M10 11v6M14 11v6'/><path d='M9 6V4h6v2'/>
              </svg>
            </div>
            <h3 style={{fontSize: 18, fontWeight: 700, color: '#0f172a', margin: '0 0 8px'}}>Delete Category</h3>
            <p style={{fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6}}>
              Are you sure you want to delete <strong style={{color: '#0f172a'}}>{deleteTarget.label}</strong>?
              This will remove it from the platform.
            </p>
            <div style={{display: 'flex', gap: 10, justifyContent: 'center'}}>
              <button onClick={() => setDeleteTarget(null)}
                style={{padding: '9px 24px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 14, cursor: 'pointer', color: '#374151'}}>
                Cancel
              </button>
              <button onClick={handleDelete}
                style={{padding: '9px 24px', borderRadius: 8, border: 'none', background: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#fff'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const InterestsPageWrapper: FC = () => <InterestsPage />

export {InterestsPageWrapper}
